import { DateTimeFormatter, Duration, LocalDateTime, LocalDate } from "@js-joda/core";

/**
 * Zero-pads a single date/time component to two digits for ISO-style string assembly.
 *
 * @param number {number} A calendar or clock component (month, day, hour, etc.). Callers pass values from js-joda getters or `JavaDateTime` parts.
 * @returns {string} The number as a two-character string (`"09"` for 9, `"10"` unchanged).
 *
 * Assumptions:
 * - Input is a non-negative integer within normal calendar ranges (1–31 for days, 0–23 for hours, etc.).
 * - Negative values are not expected from js-joda; they would stringify without a leading zero.
 *
 * Edge cases:
 * - Values ≥ 10 are returned as-is (no truncation).
 * - `NaN` propagates as the literal string `"NaN"` — callers must supply numeric components.
 */
function formatDateNumber(number: number): string {
    return number >= 10 ? `${number}` : `0${number}`;
}

/**
 * Alias for {@link formatDateNumber} used throughout this module when building YMD/HMS segments.
 *
 * @param number {number} Date or time component to pad.
 * @returns {string} Two-digit string form of `number`.
 *
 * Assumptions:
 * - Preserved as a separate symbol for source-compat with the pre-TypeScript implementation; behavior is identical to `formatDateNumber`.
 *
 * Edge cases:
 * - Same as `formatDateNumber` — no additional validation layer.
 */
function addIntForDate(number: number): string {
    return formatDateNumber(number);
}

// js-joda runtime fields prefixed with `_` (e.g. `_year`, `_month`, `_day`) are
// internal but exist on the underlying object. The original code accesses them
// directly. We preserve the exact behavior by typing the value as a plain
// number-keyed record and accessing the internal fields via bracket notation.
type LocalDateInternal = {
    year: number;
    monthValue: number;
    dayOfMonth: number;
    _year: number;
    _month: number;
    _day: number;
};

type DayOfWeekInternal = {
    name: () => string;
    _name: string;
};

type LocalDateTimeInternal = {
    year: () => number;
    monthValue: () => number;
    dayOfMonth: () => number;
    dayOfWeek: () => DayOfWeekInternal;
    month: () => { name: () => string };
    hour: () => number;
    minute: () => number;
    second: () => number;
};

/**
 * Casts a js-joda `LocalDate` to an internal shape that exposes `_year`, `_month`, and `_day` fields.
 *
 * @param d {LocalDate} A js-joda date instance.
 * @returns {LocalDateInternal} The same object, typed for bracket access to runtime-prefixed fields.
 *
 * Assumptions:
 * - js-joda continues to attach `_year` / `_month` / `_day` on the underlying object (undocumented but relied on historically).
 * - Callers need date-only YMD strings without invoking public getters that may differ by locale.
 *
 * Edge cases:
 * - If js-joda removes internal fields in a future release, `buildYmdString` and range helpers will produce `"undefined"` segments — upgrade js-joda with care.
 */
function asDate(d: LocalDate): LocalDateInternal {
    return d as unknown as LocalDateInternal;
}

/**
 * Casts a js-joda `LocalDateTime` to an internal shape with method accessors used by `formatCustomDateTime`.
 *
 * @param d {LocalDateTime} A js-joda date-time instance.
 * @returns {LocalDateTimeInternal} The same object, typed for `year()`, `monthValue()`, `dayOfWeek()`, etc.
 *
 * Assumptions:
 * - `formatCustomDateTime` is the sole consumer; it expects English weekday/month abbreviations from `.name().substring(0, 3)`.
 *
 * Edge cases:
 * - Unparseable or partial `LocalDateTime` values are not validated here — js-joda throws at parse sites upstream.
 */
function asDateTime(d: LocalDateTime): LocalDateTimeInternal {
    return d as unknown as LocalDateTimeInternal;
}

/**
 * Casts a day-of-week object to expose the internal `_name` enum string (`SATURDAY`, `SUNDAY`, …).
 *
 * @param d {{ name: () => string }} Object returned by `LocalDateTime.dayOfWeek()` (or equivalent).
 * @returns {DayOfWeekInternal} Typed view with `_name` for weekend roll-forward in `handleDateExpiry`.
 *
 * Assumptions:
 * - `_name` is uppercase English (`SATURDAY` / `SUNDAY`) as produced by js-joda's default locale.
 *
 * Edge cases:
 * - Locale changes could alter `_name` casing or spelling and break the `switch` in `handleDateExpiry`.
 */
function asDayOfWeek(d: { name: () => string }): DayOfWeekInternal {
    return d as unknown as DayOfWeekInternal;
}

// Module-level side effect: compute `tomorrow` string. The result is unused
// at runtime (the original code had the same unused-variable behavior).
// Keep the side effect to preserve identical runtime behavior.
{
    const t = asDate(LocalDate.now().plusDays(1));
    const tomorrow = `${t._year}-${addIntForDate(t._month)}-${addIntForDate(t._day)}`;
    void tomorrow;
}

/**
 * Formats a `LocalDateTime` using one of four hard-coded patterns. The supported patterns are:
 *   - "yyyy-MM-dd HH:mm:ss"  — full ISO-style local date-time (the default)
 *   - "E dd MMM, HH:mm"      — short, same-year: weekday-3-letter + day + month-3-letter + HH:mm (e.g. "Mon 09 Jun, 14:30")
 *   - "dd/MM/yyyy, HH:mm"    — long, other-year: day/month/year + HH:mm
 *   - "HH:mm"                — time only (used for "Today at HH:mm")
 *
 * @param dateTime {LocalDateTime} The js-joda date-time to render.
 * @param pattern {string} One of the four patterns above. Case-sensitive. Logs the pattern to `console.log` (preserved from the original implementation — likely a debugging aid).
 * @returns {string} The formatted string for the matching pattern.
 * @throws {Error} "Unsupported pattern" when `pattern` does not match any of the four supported values.
 *
 * Assumptions:
 * - `dateTime` is a valid `LocalDateTime` (year/month/day/hour/minute/second are all defined).
 * - The `console.log(pattern)` call is intentional legacy behavior; it is preserved.
 */
function formatCustomDateTime(dateTime: LocalDateTime, pattern: string): string {
    console.log(pattern);
    const dt = asDateTime(dateTime);
    const day: string = addIntForDate(dt.dayOfMonth());
    const month: string = addIntForDate(dt.monthValue());
    const year: number = dt.year();
    const hour: string = addIntForDate(dt.hour());
    const minute: string = addIntForDate(dt.minute());
    const second: string = addIntForDate(dt.second());

    switch (pattern) {
        case "yyyy-MM-dd HH:mm:ss":
            return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
        case "E dd MMM, HH:mm":
            return `${dt.dayOfWeek().name().substring(0, 3)} ${day} ${dt.month().name().substring(0, 3)}, ${hour}:${minute}`;
        case "dd/MM/yyyy, HH:mm":
            return `${day}/${month}/${year}, ${hour}:${minute}`;
        case "HH:mm":
            return `${hour}:${minute}`;
        default:
            throw new Error("Unsupported pattern");
    }
}

const defaultFormat: string = "yyyy-MM-dd HH:mm:ss";
const thisYearFormat: string = "E dd MMM, HH:mm";
const otherYearFormat: string = "dd/MM/yyyy, HH:mm";
const todayFormat: string = "HH:mm";

interface DatePart {
    year: number;
    month: number;
    day: number;
}

interface TimePart {
    hour: number;
    minute: number;
    second?: number;
}

export interface JavaDateTime {
    date: DatePart;
    time: TimePart;
}

/**
 * Serialises a backend `JavaDateTime` JSON shape into the module's canonical parse string (`yyyy-MM-dd HH:mm:ss`).
 *
 * @param dateObject {JavaDateTime} Object with `date: { year, month, day }` and `time: { hour, minute, second? }` as returned by Yango report APIs.
 * @returns {string} Space-separated local date-time suitable for `LocalDateTime.parse` with pattern `yyyy-MM-dd HH:mm:ss`.
 *
 * Assumptions:
 * - Month and day are 1-based (Java `LocalDate` convention), not zero-based.
 * - Missing `time.second` defaults to `0` (midnight boundary for expiry calculations).
 *
 * Edge cases:
 * - No null-guard on `dateObject` — callers must pass a populated object or parsing downstream will throw.
 * - Invalid component values (e.g. month 13) are not validated; js-joda may throw on parse.
 */
function parseDate(dateObject: JavaDateTime): string {
    const date: DatePart = dateObject.date;
    const time: TimePart = dateObject.time;
    return `${date.year}-${formatDateNumber(date.month)}-${formatDateNumber(date.day)} ${formatDateNumber(time.hour)}:${formatDateNumber(time.minute)}:${formatDateNumber(time.second ?? 0)}`;
}

/**
 * Adjusts an invoice expiry date string so it never falls on a weekend — rolls Saturday → Friday, Sunday → Friday.
 *
 * @param dateString {string} A `yyyy-MM-dd` date (time portion added internally as `T00:00:00` before parsing).
 * @returns {string} The adjusted date in `yyyy-MM-dd` form (time stripped via `.slice(0, -9)` after formatting).
 *
 * Assumptions:
 * - Business rule: monthly/yearly expiries target the 7th but must land on a weekday; weekend dates move backward to the preceding Friday.
 * - Input is a valid ISO local date without time; the function appends `T00:00:00` for js-joda parsing.
 *
 * Edge cases:
 * - Monday–Friday dates pass through unchanged (only `SATURDAY` and `SUNDAY` branches fire).
 * - The `.slice(0, -9)` strip depends on `defaultFormat` ending with ` HH:mm:ss` — changing that constant breaks expiry output length.
 */
function handleDateExpiry(dateString: string): string {
    dateString = dateString + 'T00:00:00';
    let date: LocalDateTime = LocalDateTime.parse(dateString);
    const dowName = asDayOfWeek(date.dayOfWeek())._name;
    switch (dowName) {
        case 'SATURDAY':
            date = date.minusDays(1);
            break;
        case 'SUNDAY':
            date = date.minusDays(2);
            break;
    }
    return formatCustomDateTime(date, defaultFormat).slice(0, -9);
}

/**
 * Builds a date-only `yyyy-MM-dd` string from a js-joda `LocalDate` via internal `_year` / `_month` / `_day` fields.
 *
 * @param d {LocalDate} Source date (typically `LocalDate.now()` or an offset such as `minusDays(7)`).
 * @returns {string} Date-only bound used by report range helpers (`getTodayRange`, `getLastMonthRange`, etc.).
 *
 * Assumptions:
 * - Used wherever the backend expects `from` / `to` as `yyyy-MM-dd` without time (see `getLastMonthRange` wire-format contract).
 * - Prefer this over `formatCustomDateTime` for SQL BETWEEN bounds — the backend appends ` 00:00:00` / ` 23:59:59` itself.
 *
 * Edge cases:
 * - Relies on {@link asDate} internal fields; same js-joda upgrade risk as other `_`-prefixed accessors.
 */
function buildYmdString(d: LocalDate): string {
    const di = asDate(d);
    return `${di._year}-${addIntForDate(di._month)}-${addIntForDate(di._day)}`;
}

export const dateTimeManager = {
    /**
     * Formats a JavaDateTime's date portion as "YYYY-MM-DD".
     *
     * @param dateObject {JavaDateTime} Object containing a `date: { year, month, day }` and a `time` field.
     *                              The `time` field is ignored by this formatter.
     * @returns {string} A zero-padded ISO-style date string in the form "YYYY-MM-DD".
     *
     * Assumptions:
     * - `dateObject` is a well-formed JavaDateTime with a numeric `date.year`, `date.month` (1-12) and `date.day` (1-31).
     * - No validation is performed; malformed input will produce a string with whatever the values were zero-padded to two digits where applicable.
     *
     * Edge cases:
     * - Months and days less than 10 are zero-padded (e.g. March becomes "03").
     * - The `time` portion of `dateObject` is intentionally ignored.
     */
    formatJavaDate(dateObject: JavaDateTime): string {
        const date: DatePart = dateObject.date;
        return `${date.year}-${formatDateNumber(date.month)}-${formatDateNumber(date.day)}`;
    },
    /**
     * Formats a JavaDateTime as an ISO 8601 local date-time string ("YYYY-MM-DDTHH:MM"), suitable for `<input type="datetime-local">` values.
     *
     * @param dateObject {JavaDateTime | null | undefined} The object to format. If falsy, returns the literal string "Invalid Date".
     * @returns {string} A formatted "YYYY-MM-DDTHH:MM" string, or "Invalid Date" when `dateObject` is null/undefined.
     *
     * Assumptions:
     * - `dateObject` is a well-formed JavaDateTime.
     * - The `time.second` field is not represented in the output.
     *
     * Edge cases:
     * - Returns "Invalid Date" when `dateObject` is null or undefined (defensive — see callers that may pass missing data).
     * - Hours, minutes, months and days are zero-padded.
     */
    formatJavaDateTime(dateObject: JavaDateTime): string {
        if (!dateObject) {
            return `Invalid Date`;
        }
        const date: DatePart = dateObject.date;
        const time: TimePart = dateObject.time;
        return `${date.year}-${formatDateNumber(date.month)}-${formatDateNumber(date.day)}T${formatDateNumber(time.hour)}:${formatDateNumber(time.minute)}`;
    },

    /**
     * Adds a number of hours to a JavaDateTime and returns the resulting date-time in the default format ("yyyy-MM-dd HH:mm:ss").
     *
     * @param dateObject {JavaDateTime | null | undefined} The source date. If falsy, returns "Invalid Date".
     * @param duration {number | string | undefined} Hours to add. If falsy (null/undefined/0/""), defaults to 2. Strings are coerced via `Number()`.
     * @returns {string} The shifted date-time in "yyyy-MM-dd HH:mm:ss" form, or "Invalid Date" when input is missing.
     *
     * Assumptions:
     * - `dateObject.date` and `dateObject.time` are valid numeric components.
     * - The runtime "default of 2 hours when missing" is a preserved business rule; changing it would affect expiry calculations.
     *
     * Edge cases:
     * - Non-numeric strings passed to `duration` will yield `NaN` after `Number()` and may throw inside `plusHours`; callers should pass numeric values.
     */
    addHoursToDate(dateObject: JavaDateTime, duration: number | string | undefined): string {
        if (!dateObject) {
            return `Invalid Date`;
        }

        if (!duration) {
            duration = 2;
        }
        const parsedDate: string = parseDate(dateObject);
        const date: LocalDateTime = LocalDateTime.parse(parsedDate, DateTimeFormatter.ofPattern("yyyy-MM-dd HH:mm:ss"))
            .plusHours(Number(duration));
        return formatCustomDateTime(date, defaultFormat);
    },

    /**
     * Renders a JavaDateTime as a short display string by parsing it through the JavaScript `Date` constructor and slicing the resulting toString() output.
     *
     * @param dateObject {JavaDateTime} The date to display. No null-guard — callers must pass a valid object.
     * @returns {string} A substring of `new Date(...).toString()` from index 3 to 20 (e.g. "Jun 09 2026 14:30:00" — locale-dependent).
     *
     * Assumptions:
     * - The browser's `Date` constructor can parse the "yyyy-MM-dd HH:mm:ss" string in the local time zone.
     * - The slice indices (3, 21) match the historical output format ("Mon DD YYYY HH:MM:SS" without the weekday and timezone).
     *
     * Edge cases:
     * - Time zone shifts between the local browser and the Yango server may cause the visible day to differ by one.
     * - This function does not localize.
     */
    dateForDisplay(dateObject: JavaDateTime): string {
        const formattedDate: string = parseDate(dateObject);
        return new Date(formattedDate).toString().slice(3, 21);
    },
    /**
     * Returns a human-friendly relative date label. Same calendar day → "Today at HH:mm"; same year → "E dd MMM, HH:mm"; other years → "dd/MM/yyyy, HH:mm".
     *
     * @param dateObject {JavaDateTime} The date to render.
     * @returns {string} A formatted short display string.
     *
     * Assumptions:
     * - `now` is captured at call time, so successive calls within the same second are deterministic.
     * - The JavaDateTime represents a wall-clock time in the browser's local zone.
     *
     * Edge cases:
     * - No null-guard on `dateObject`; passing null will throw in `parseDate`.
     */
    shortenDisplay(dateObject: JavaDateTime): string {
        const parsedDate: string = parseDate(dateObject);
        const formattedDate: LocalDateTime = LocalDateTime.parse(parsedDate, DateTimeFormatter.ofPattern("yyyy-MM-dd HH:mm:ss"));
        const now: LocalDateTime = LocalDateTime.now();
        if (formattedDate.toLocalDate().equals(now.toLocalDate())) {
            return `Today at ` + formatCustomDateTime(formattedDate, todayFormat);
        } else if (formattedDate.toLocalDate().year() === now.toLocalDate().year()) {
            return formatCustomDateTime(formattedDate, thisYearFormat);
        } else {
            return formatCustomDateTime(formattedDate, otherYearFormat);
        }
    },
    /**
     * Same heuristic as `shortenDisplay` but accepts a pre-parsed ISO-style string instead of a JavaDateTime object.
     *
     * @param parsedDate {string | null} An ISO 8601 local date-time string ("YYYY-MM-DDTHH:MM:SS" or "YYYY-MM-DD HH:MM:SS"). `null` returns `null` unchanged.
     * @returns {string | null} The friendly short label, or `null` if `parsedDate` is `null`.
     *
     * Assumptions:
     * - Input is well-formed; js-joda will throw on unparseable values.
     * - Both "T" and " " separators are accepted (the function normalizes "T" → " ").
     *
     * Edge cases:
     * - `null` input is the only defensive branch and is returned as-is.
     */
    shortenStandardDisplay(parsedDate: string | null): string | null {
        if (!parsedDate) return null;
        parsedDate = parsedDate.replaceAll("T", " ");
        const formattedDate: LocalDateTime = LocalDateTime.parse(parsedDate, DateTimeFormatter.ofPattern("yyyy-MM-dd HH:mm:ss"));
        const now: LocalDateTime = LocalDateTime.now();
        if (formattedDate.toLocalDate().equals(now.toLocalDate())) {
            return `Today at ` + formatCustomDateTime(formattedDate, todayFormat);
        } else if (formattedDate.toLocalDate().year() === now.toLocalDate().year()) {
            return formatCustomDateTime(formattedDate, thisYearFormat);
        } else {
            return formatCustomDateTime(formattedDate, otherYearFormat);
        }
    },

    /**
     * Returns the first second of the current month formatted in the default pattern. Useful as a "from" timestamp for "this month" filters.
     *
     * @returns {string} Current month's first day at 00:00:00 in "yyyy-MM-dd HH:mm:ss" form.
     */
    getStartOfThisMonth(): string {
        return formatCustomDateTime(LocalDateTime.now().withDayOfMonth(1), defaultFormat);
    },

    /**
     * Returns the current local date-time in the default pattern.
     *
     * @returns {string} "now" formatted as "yyyy-MM-dd HH:mm:ss".
     */
    getNow(): string {
        return formatCustomDateTime(LocalDateTime.now(), defaultFormat);
    },

    /**
     * Returns the local date-time 24 hours from now in the default pattern.
     *
     * @returns {string} "now + 1 day" formatted as "yyyy-MM-dd HH:mm:ss".
     */
    getTomorrow(): string {
        return formatCustomDateTime(LocalDateTime.now().plusDays(1), defaultFormat);
    },

    /**
     * Returns the local date-time seven days from now in the default pattern. Used by date-range pickers in the reports UI.
     *
     * @returns {string} "now + 7 days" formatted as "yyyy-MM-dd HH:mm:ss".
     */
    getNext7Days(): string {
        return formatCustomDateTime(LocalDateTime.now().plusDays(7), defaultFormat);
    },

    /**
     * Returns the local date-time 24 hours ago in the default pattern. Used as the upper bound of "yesterday" reports and as a fallback for billing windows.
     *
     * @returns {string} "now - 1 day" formatted as "yyyy-MM-dd HH:mm:ss".
     */
    getYesterday(): string {
        return formatCustomDateTime(LocalDateTime.now().minusDays(1), defaultFormat);
    },

    /**
     * Computes the next monthly invoice expiry date. Takes "now + 1 month", rewrites the day to "07", and rolls the date forward off weekends via `handleDateExpiry`.
     *
     * @returns {string} The expiry date in "yyyy-MM-dd" form (the `handleDateExpiry` `.slice(0, -9)` strips the time portion).
     *
     * Assumptions:
     * - Business rule: monthly expiries always fall on the 7th, and weekends roll to the preceding Friday.
     */
    getNextMonthlyExpiryDate(): string {
        const dateString: string = formatCustomDateTime(LocalDateTime.now().plusMonths(1), defaultFormat).slice(0, -11) + '07';
        return handleDateExpiry(dateString);
    },

    /**
     * Computes the next yearly invoice expiry date. Same shape as `getNextMonthlyExpiryDate` but uses "+ 1 year" as the base.
     *
     * @returns {string} The expiry date in "yyyy-MM-dd" form, with weekend roll-forward applied.
     */
    getNextYearlyExpiryDate(): string {
        const dateString: string = formatCustomDateTime(LocalDateTime.now().plusYears(1), defaultFormat).slice(0, -11) + '07';
        return handleDateExpiry(dateString);
    },

    /**
     * Builds a "today only" date range with both `from` and `to` set to today's YMD string. Useful for default filter presets.
     *
     * @returns {{ from: string, to: string }} Object with both bounds equal to today's "YYYY-MM-DD".
     */
    getTodayRange(): { from: string; to: string } {
        const today: string = buildYmdString(LocalDate.now());
        return { from: today, to: today };
    },

    /**
     * Builds a "yesterday only" date range with both `from` and `to` set to yesterday's YMD string.
     *
     * @returns {{ from: string, to: string }} Object with both bounds equal to yesterday's "YYYY-MM-DD".
     */
    getYesterdayRange(): { from: string; to: string } {
        const yesterday: string = buildYmdString(LocalDate.now().minusDays(1));
        return { from: yesterday, to: yesterday };
    },

    /**
     * Builds a 7-day trailing range. `from` = 7 days ago; `to` = today. Note the asymmetry: this is "last 7 days" not "last week (Mon-Sun)".
     *
     * @returns {{ from: string, to: string }} 7-day trailing window in "YYYY-MM-DD" form.
     */
    getLastWeekRange(): { from: string; to: string } {
        const today: string = buildYmdString(LocalDate.now());
        const startOfWeek: string = buildYmdString(LocalDate.now().minusDays(7));
        return { from: startOfWeek, to: today };
    },

    /**
     * Builds the previous calendar month's full range. `from` = first of last month; `to` = last day of last month.
     *
     * Wire-format contract (matches BE `InvoiceReportsLogic.getInvoices` lines 35-36 in yangoAPI):
     *   - `from` is "yyyy-MM-dd" — BE appends " 00:00:00" before the SQL BETWEEN.
     *   - `to`   is "yyyy-MM-dd" — BE appends " 23:59:59" before the SQL BETWEEN.
     * Therefore both bounds MUST be date-only ("yyyy-MM-dd"). Do NOT use `formatCustomDateTime`
     * here — that would produce a full date-time literal which, when concatenated with the BE
     * suffix, would yield an invalid SQL literal (e.g. "2025-05-31 00:00:00 23:59:59"). Use
     * `buildYmdString` (date-only) instead.
     *
     * @returns {{ from: string, to: string }} Previous month range in "yyyy-MM-dd" form for both bounds.
     *
     * Assumptions:
     * - `LocalDate.now().minusMonths(1)` resolves to a valid LocalDate (handles month-rollover end-to-end).
     * - `lengthOfMonth()` correctly returns 28/29/30/31 depending on the resolved month.
     */
    getLastMonthRange(): { from: string; to: string } {
        const lastMonthDate: LocalDate = LocalDate.now().minusMonths(1);
        const numberOfDays: number = lastMonthDate.lengthOfMonth();
        const lastDayOfMonth: string = buildYmdString(lastMonthDate.withDayOfMonth(numberOfDays));

        const from: string = buildYmdString(lastMonthDate.withDayOfMonth(1));
        return { from, to: lastDayOfMonth };
    },

    /**
     * Builds the current calendar month's range so far. `from` = first of this month; `to` = today.
     *
     * @returns {{ from: string, to: string }} Range covering from the 1st of the current month through today.
     */
    getThisMonthRange(): { from: string; to: string } {
        const today: string = buildYmdString(LocalDate.now());
        const now: LocalDate = LocalDate.now();
        const firstDayOfMonth: string = `${asDate(now)._year}-${addIntForDate(asDate(now)._month)}-01`;
        return { from: firstDayOfMonth, to: today };
    },

    /**
     * Builds the current calendar year's range so far. `from` = January 1; `to` = today.
     *
     * @returns {{ from: string, to: string }} Range covering from Jan 1 of the current year through today.
     */
    getThisYearRange(): { from: string; to: string } {
        const today: string = buildYmdString(LocalDate.now());
        const startOfYear: string = `${asDate(LocalDate.now())._year}-01-01`;
        return { from: startOfYear, to: today };
    },

    /**
     * Returns a "now + 2 days" string suitable for HTML `<input type="datetime-local">` default values. The output is the default pattern with the space replaced by "T" and the seconds stripped.
     *
     * @returns {string} "now + 2 days" in "YYYY-MM-DDTHH:MM" form.
     */
    getNextTwoDaysForForm(): string {
        return formatCustomDateTime(LocalDateTime.now().plusDays(2), defaultFormat)
            .replace(' ', 'T')
            .slice(0, -3);
    },

    /**
     * Returns a "now + 1 week" string suitable for HTML `<input type="datetime-local">` default values.
     *
     * @returns {string} "now + 1 week" in "YYYY-MM-DDTHH:MM" form.
     */
    getNextWeekForForm(): string {
        return formatCustomDateTime(LocalDateTime.now().plusWeeks(1), defaultFormat)
            .replace(' ', 'T')
            .slice(0, -3);
    },

    /**
     * Diagnostic helper: logs the duration between a JavaDateTime and "now" in multiple js-joda formats. Currently a console-log only; the result is not returned.
     *
     * @param dateObject {JavaDateTime} The past date to compare.
     * @param showAgoIfLonger {boolean} Reserved/unused. Preserved on the signature for source compatibility; explicitly `void`-ed at runtime.
     * @returns {void}
     *
     * Assumptions:
     * - Caller accepts that this function is a debugging aid — it never throws on `showAgoIfLonger` being any value.
     */
    timeAgo(dateObject: JavaDateTime, showAgoIfLonger: boolean): void {
        void showAgoIfLonger;
        const parsedDate: string = parseDate(dateObject);
        const date: LocalDateTime = LocalDateTime.parse(parsedDate, DateTimeFormatter.ofPattern("yyyy-MM-dd HH:mm:ss"));
        const now: LocalDateTime = LocalDateTime.now();
        const duration: Duration = Duration.between(date, now);
        console.log(duration);
        console.log(duration.toJSON());
        console.log(duration.toString());
        if (duration.toDays() > 1) {
            console.log("Duration greater than 1: " + duration.toDays());
        } else if (duration.toDays() === 1) {
            console.log("Duration greater equal to 1: " + duration.toDays());
        } else {
            console.log("Duration greater equal to 1: " + duration.toDays());
        }
    },

    /**
     * Compares a date string or `Date` instance against the current time. Returns true when the given date is strictly before now.
     *
     * @param date {string | Date} Either a parseable date string (any format the JS `Date` constructor accepts) or a `Date` instance.
     * @returns {boolean} `true` when `date` is strictly earlier than the current moment, `false` otherwise (including equal-to-now).
     *
     * Edge cases:
     * - Invalid date strings produce a `Date` whose `valueOf()` is `NaN`; comparisons against `NaN` are always `false`, so this returns `false` for unparseable input.
     * - String parsing is delegated to the JS `Date` constructor and inherits its quirks (e.g. ISO 8601 is preferred).
     */
    hasDatePassed(date: string | Date): boolean {
        const now: Date = new Date();
        const other: Date = new Date(date);
        return now > other;
    },
};
